import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SIGN_IN_PATH, authConfigured } from '../api/auth';
import { useSession } from '../api/token';

const NAV = [
  { to: '/capture', label: 'Capture' },
  { to: '/review', label: 'Review' },
  { to: '/export', label: 'Export' },
  { to: '/account', label: 'Account' },
] as const;

/**
 * The app frame: brand, nav, session controls, and the ONE place the unauthenticated /
 * unauthorised states are announced.
 *
 * What used to be here was Clerk's `<SignedIn>/<SignedOut>/<UserButton>` plus a permanent
 * "Auth not configured — set VITE_CLERK_PUBLISHABLE_KEY" banner that showed whenever that key was
 * missing — i.e. the app's steady state was an error about a provider we no longer use. The three
 * states below are real session states instead:
 *
 *   signed-out — no session; a normal first visit. Invitation to sign in, not an error.
 *   expired    — a session was rejected (401) or had already lapsed. `role="alert"`: the coach was
 *                interrupted mid-work and needs to know why before signing in again.
 *   unavailable — only when the build's issuer origin is unusable, which the default cannot be.
 */
export function AppShell() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const onSignInRoute = location.pathname.startsWith(SIGN_IN_PATH);

  async function onSignOut() {
    setSigningOut(true);
    // Revokes at the issuer (the contract the verifiers honour), then clears local state.
    await session.signOut();
    setSigningOut(false);
    navigate(SIGN_IN_PATH, { replace: true });
  }

  const label = session.user?.email ?? session.user?.name ?? 'signed in';

  return (
    <div className="flex min-h-full flex-col">
      <header
        className="border-b border-chalk-200 bg-charcoal text-chalk"
        data-testid="auth-state"
        data-status={session.status}
        data-issuer-configured={authConfigured() ? 'true' : 'false'}
      >
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-6">
          <span className="flex items-center gap-2 font-semibold tracking-tight">
            <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
              <path
                d="M20 40V24l12 8 12-8v16"
                stroke="#C87A3A"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            Callout
          </span>
          <nav className="flex flex-wrap gap-1 text-sm">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded px-3 py-1.5 transition-colors ${
                    isActive ? 'bg-copper text-charcoal' : 'text-chalk-200 hover:bg-charcoal-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {session.status === 'signed-in' ? (
              <>
                <span className="font-mono text-chalk-200" data-testid="auth-user">
                  {label}
                </span>
                <button
                  type="button"
                  data-testid="signout"
                  disabled={signingOut}
                  onClick={() => void onSignOut()}
                  className="rounded border border-chalk-200/40 px-3 py-1.5 font-medium text-chalk disabled:opacity-60"
                >
                  {signingOut ? 'Signing out…' : 'Sign out'}
                </button>
              </>
            ) : (
              <NavLink
                to={SIGN_IN_PATH}
                data-testid="signin-link"
                className="rounded bg-copper px-3 py-1.5 font-medium text-charcoal"
              >
                Sign in
              </NavLink>
            )}
          </div>
        </div>
      </header>

      {!authConfigured() && (
        <p
          role="alert"
          data-testid="auth-unavailable"
          className="bg-copper-300/40 px-6 py-2 text-center text-sm text-charcoal-900"
        >
          Sign-in is unavailable: this build has no auth issuer (<code className="font-mono">VITE_AUTH_BASE</code>).
        </p>
      )}

      {authConfigured() && session.status === 'expired' && (
        <p
          role="alert"
          data-testid="auth-expired"
          className="bg-red-50 px-6 py-2 text-center text-sm text-red-800"
        >
          Your session has ended — it expired or was signed out elsewhere.{' '}
          <NavLink to={SIGN_IN_PATH} className="underline">
            Sign in again
          </NavLink>{' '}
          to keep tagging. Nothing you already tagged was lost.
        </p>
      )}

      {authConfigured() && session.status === 'signed-out' && !onSignInRoute && (
        <p
          role="status"
          data-testid="auth-signed-out"
          className="bg-copper-300/40 px-6 py-2 text-center text-sm text-charcoal-900"
        >
          You are not signed in.{' '}
          <NavLink to={SIGN_IN_PATH} className="underline">
            Sign in
          </NavLink>{' '}
          to upload footage and record sessions.
        </p>
      )}

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        <Outlet />
      </main>

      <footer className="border-t border-chalk-200 px-6 py-4 text-center text-xs text-charcoal-500">
        <p>Callout · voice-first video tagging · coaches control what is exported</p>
        <p className="mt-1">
          {/* Safeguarding §3.3.5: the privacy link lives in the footer of every page. */}
          <NavLink to="/privacy" className="text-copper-700 underline" data-testid="footer-privacy-link">
            Privacy
          </NavLink>
          <span className="mx-2 text-charcoal-400">·</span>
          <NavLink to="/account" className="text-copper-700 underline" data-testid="footer-account-link">
            Your data
          </NavLink>
        </p>
      </footer>
    </div>
  );
}
